import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { MatButtonToggleGroup } from '@angular/material/button-toggle';
import { ActivatedRoute, Router } from '@angular/router';
import {
  CompanyViewModel,
  CreateFillSiteViewModel,
  CreateSourceSiteViewModel,
  ICreateCompanyCommand,
  ICreateQualifiedPersonCommand,
  LegalEntityStatus,
  LegalEntityType,
  QualifiedPersonViewModel,
  SiteStatus
} from '@services/cleanfill-api/cleanfill-api.generated';
import { StoreRegistrationService } from '@services/cleanfill-core/store-registration/store-registration.service';
import { StoreUsersService } from '@services/cleanfill-core/store-users/store-users.service';
import { AuthService } from '@services/core/auth.service';
import { CompanyType } from '@view-models/registration/Registration';
import { ConfirmDialogResult, ConfirmDialogType } from '@widgets/site-common/confirm-dialog/confirm-dialog.model';
import { ConfirmDialogService } from '@widgets/site-common/confirm-dialog/confirm-dialog.service';
import { Observable, Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';

@Component({
  selector: 'register-business-type',
  templateUrl: './register-business-type.component.html',
  styleUrls: ['./register-business-type.component.scss']
})
export class RegisterBusinessTypeComponent implements OnInit, OnDestroy {
  CompanyType = CompanyType;
  id: string;
  type: string;
  subscriptions = new Subscription();
  company$: Observable<CompanyViewModel>;
  qp$: Observable<QualifiedPersonViewModel>;
  @ViewChild(MatButtonToggleGroup) registrationType: MatButtonToggleGroup;
  selectedTypes = [];
  constructor(
    private storeRegistration: StoreRegistrationService,
    private router: Router,
    private route: ActivatedRoute,
    private confirmDialogService: ConfirmDialogService,
    private userService: StoreUsersService,
    private authService: AuthService
  ) {
    if (this.route?.snapshot) {
      this.id = this.route.snapshot.params.id || '';
      this.type = this.route.snapshot.params.type || '';
      if (this.id !== '' && this.id !== 'new' && this.type !== '') {
        if (this.type === 'company') {
          this.storeRegistration.getCompany(this.id);
          this.company$ = storeRegistration.company$.pipe(filter(c => c != null));
        } else {
          this.storeRegistration.getQualifiedPerson(this.id);
          this.qp$ = storeRegistration.qp$.pipe(filter(q => q != null));
        }
      }
    }
  }

  selectionChanged(change) {
    if (this.id && this.id !== 'new') {
      this.confirmDialogService
        .confirm(
          'Changing your selection will restart the company creation process and all your previous entries will be deleted.',
          'Are you sure you want to change your selection?',
          ConfirmDialogType.YesNo
        )
        .then(t => {
          if (t === ConfirmDialogResult.Yes) {
            if (this.type === 'company') {
              this.storeRegistration.deleteCompany(this.id, () => {
                this.id = '';
                this.router.navigateByUrl('/register/choose/company/new');
                this.switchToQP(change);
              });
            } else {
              this.storeRegistration.deleteQualifiedPerson(this.id, () => {
                this.id = '';
                this.router.navigateByUrl('/register/choose/company/new');
                this.switchToQP(change);
              });
            }
          } else {
            this.registrationType.value = this.selectedTypes;
          }
        });
      return;
    } else {
      this.switchToQP(change);
    }
  }

  switchToQP(change) {
    if (change.value.find(p => p === CompanyType.QualifiedPerson)) {
      this.registrationType.value = (this.registrationType.value as CompanyType[]).slice(-1);
    }
  }
  ngOnInit(): void {
    let sub: Subscription;
    if (this.type === 'company') {
      sub = this.getCompanySubscription();
    } else {
      sub = this.getQpSubscription();
    }
    if (sub) {
      this.subscriptions.add(sub);
    }
  }

  getQpSubscription() {
    return this.qp$?.subscribe(async qp => {
      if (qp.status === LegalEntityStatus.Active) {
        await this.router.navigateByUrl('/');
      } else {
        if (this.registrationType) {
          this.selectedTypes = [CompanyType.QualifiedPerson];
          this.registrationType.value = [CompanyType.QualifiedPerson];
        }
      }
    });
  }
  getCompanySubscription() {
    return this.company$?.subscribe(async comp => {
      if (comp.status === LegalEntityStatus.Active) {
        await this.router.navigateByUrl('/');
      } else {
        const types = [];
        if (comp.legalEntityType === LegalEntityType.Company) {
          if (comp.fillSites.length > 0) {
            types.push(CompanyType.ReceivingSite);
          }
          if (comp.sourceSites.length > 0) {
            types.push(CompanyType.SourceSite);
          }
        }
        if (this.registrationType) {
          this.selectedTypes = types;
          this.registrationType.value = types;
        }
      }
    });
  }
  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }
  next() {
    this.isLoggedIn().then(t => {
      if (t === false) {
        if ((this.registrationType.value as CompanyType[]).length > 0) {
          if (this.registrationType.value.find(r => r === CompanyType.QualifiedPerson)) {
            this.setupQp();
          } else if (this.registrationType.value.find(r => r === CompanyType.SourceSite || r === CompanyType.ReceivingSite)) {
            this.setupCompany();
          }
        }
      }
    });
  }

  setupQp() {
    const qpModel: ICreateQualifiedPersonCommand = {};
    qpModel.status = LegalEntityStatus.Draft;
    if (this.id !== 'new' && this.id !== '') {
      this.router.navigate(['register', 'qp', this.id]);
    } else {
      this.storeRegistration.createQualifiedPerson(qpModel, t => {
        this.router.navigate(['register', 'qp', t.uniqueId]);
      });
    }
  }
  setupCompany() {
    const companyModel: ICreateCompanyCommand = {};
    if (this.registrationType.value.find(r => r === CompanyType.SourceSite)) {
      companyModel.sourceSites = [new CreateSourceSiteViewModel({ name: '', status: SiteStatus.Active })];
    }
    if (this.registrationType.value.find(r => r === CompanyType.ReceivingSite)) {
      companyModel.fillSites = [new CreateFillSiteViewModel({ name: '', status: SiteStatus.Active })];
    }
    companyModel.status = LegalEntityStatus.Draft;
    if (this.id !== 'new' && this.id !== '') {
      this.router.navigate(['register', 'company', this.id]);
    } else {
      this.storeRegistration.createCompany(companyModel, t => {
        this.router.navigate(['register', 'company', t.uniqueId]);
      });
    }
  }
  isLoggedIn() {
    if (this.authService.activeUser !== null) {
      if (this.authService.activeUser.legalEntityStatus === LegalEntityStatus.Active) {
        return this.confirmDialogService
          .confirm(
            `You are already registered with an active company.
            You will now be logged out from this company and can restart the registration process. `,
            '',
            ConfirmDialogType.Ok
          )
          .then(result => {
            if (result === ConfirmDialogResult.Ok) {
              this.authService.logout();
              // this.router.navigate(['/']);
              return true;
            }
          });
      }
    }
    return Promise.resolve(false);
  }
}
